import { useFleetContracts, useUser } from '@cobliteam/hooks';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

import LoadingPage from '../pages/LoadingPage';

interface FeatureFlagsProviderProps {
  children: React.ReactNode;
}

const COMPANY_ID_STORAGE_KEY = 'companyId';

const getCompanyIdCacheKey = (fleetId: string) => `${COMPANY_ID_STORAGE_KEY}_${fleetId}`;

const FeatureFlagsProvider = ({ children }: FeatureFlagsProviderProps) => {
  const ldClient = useLDClient();
  const [initialized, setInitialized] = useState(false);

  const { session, useGetUserData } = useUser();
  const { data: user } = useGetUserData(session?.user.id, !!session);
  const { data: fleetContracts } = useFleetContracts(session?.fleet.id || '', { enabled: !!session?.fleet.id });

  // Get companyId from cache or API response
  const getCompanyIds = useCallback((): string[] => {
    const cachedCompanyId = typeof window !== 'undefined' ? window.localStorage.getItem(getCompanyIdCacheKey(session!.fleet.id)) : null;
    const cachedCompanyIds = cachedCompanyId ? cachedCompanyId.split(',') : [];
    return cachedCompanyIds.length > 0 ? cachedCompanyIds : (fleetContracts?.map((contract) => contract.companyId) ?? []);
  }, [session, fleetContracts]);

  // Update cache when fleetContracts arrives
  useEffect(() => {
    if (fleetContracts?.[0]?.companyId && session) {
      const cacheKey = getCompanyIdCacheKey(session.fleet.id);
      window.localStorage.setItem(cacheKey, fleetContracts.map((contract) => contract.companyId).join(','));
    }
  }, [fleetContracts, session]);

  // Clear cache when fleet changes
  useEffect(() => {
    if (!session) return;

    const clearOldFleetCache = () => {
      // Clean up localStorage keys from previous fleets (optional cleanup)
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(COMPANY_ID_STORAGE_KEY)) {
          keysToRemove.push(key);
        }
      }
      // Keep only current fleet cache, remove old ones
      for (const key of keysToRemove) {
        if (key !== getCompanyIdCacheKey(session.fleet.id)) {
          window.localStorage.removeItem(key);
        }
      }
    };

    clearOldFleetCache();
  }, [session?.fleet.id, session]);

  const ldClientIdentify = useCallback(async () => {
    if (ldClient && user && session) {
      const companyIds = getCompanyIds();

      await ldClient.identify({
        kind: 'multi',
        user: {
          key: session.user.email,
          id: session.user.id,
          fleet_name: session.fleet.name,
          fleet_id: session.fleet.id,
          num_devices: user.userAttributes.numberOfDevices,
          creation_date: user.userAttributes.creationDate,
          age_in_days: user.userAttributes.ageInDays,
        },
        fleet: {
          key: session.fleet.id,
          name: session.fleet.name,
          num_devices: user.userAttributes.numberOfDevices,
          company_ids: companyIds,
        },
      });

      setInitialized(true);
    }
  }, [ldClient, user, session, getCompanyIds]);

  useEffect(() => {
    ldClientIdentify();
  }, [ldClientIdentify]);

  if (!initialized) return <LoadingPage showLogo message='Carregando configurações do sistema...' />;

  return children;
};

export default FeatureFlagsProvider;
